import {Component, Input, OnInit, OnChanges, Output, EventEmitter, ViewChild, ElementRef, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, switchMap, map, merge } from 'rxjs/operators';
import { AlgebraKiTService } from "../../services/algebrakit.service";
import { AkitCreateSessionResult, IErrorData, AkitLearningEvent, AkitPristineChangedEvent, AkitLearningEventType } from "../../types/algebrakit.types";

declare const AlgebraKIT: any;

@Component({
    selector: "app-akit-interaction",
    templateUrl: "./akit-interaction.component.html",
    styleUrls: ["./akit-interaction.component.less"],
    standalone: false
})
export class AkitInteractionComponent implements OnInit, OnChanges {
    private _sessionId = new BehaviorSubject<string>(null);
    public _refId:string;

    public showSessionId: string; //this sessionId will be used in the template
    public session: AkitCreateSessionResult; //the session data for the current exercise

    // restore an existing session
    @Input() set sessionId(sessionId: string) {
        if (sessionId) {
            //first remove the old exercise, because you cannot dynamically change the session-id in AlgebraKiT
            this.rehydrate(sessionId);
        }
    }

    @Input() set refId(refId: string) {
        if (refId) {
            this._refId = refId;
            //first remove the old exercise, because you cannot dynamically change the session-id in AlgebraKiT
            this.rehydrate();
        }
    }

    @Input() solutionMode: boolean;
    @Input() reviewMode: boolean;

    @Output() activated: EventEmitter<void> = new EventEmitter<void>();
    @Output() learningEvent: EventEmitter<AkitLearningEvent> =
        new EventEmitter<AkitLearningEvent>();
    @Output() pristineChanged: EventEmitter<AkitPristineChangedEvent> =
        new EventEmitter<AkitPristineChangedEvent>(); //input changed yes/no
    @Output() error: EventEmitter<IErrorData> = new EventEmitter<IErrorData>();

    @Output() initialized: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild("container") container: ElementRef;

    renderPromise: Promise<void>;
    renderResolve: () => void; //to resolve promise after render

    constructor(
        private akitService: AlgebraKiTService,
        private _ngZone: NgZone
    ) {
        this._sessionId
            .subscribe(async sessionId => {
                this.showSessionId = sessionId;
                if (!sessionId) return;

                AlgebraKIT.addExerciseListener(sessionId, obj => {
                    this._ngZone.run(() => {
                        if (
                            obj.sessionId == sessionId && obj.refId == this.refId &&
                            obj.data && !obj.data.replay
                        ) {
                            switch (obj.event) {
                                case "interaction-evaluate": {
                                    this.learningEvent.emit({
                                        type: AkitLearningEventType.EVALUATE,
                                        marksTotal: obj.data.scoring?obj.data.scoring.marksTotal:-1,
                                        marksEarned:
                                            obj.data.scoring?obj.data.scoring.marksEarned:-1,
                                        finished: obj.data.scoring?obj.data.scoring.finished:false,
                                        tags: obj.data.tags,
                                    });
                                    break;
                                }
                                case "interaction-hint": {
                                    this.learningEvent.emit({
                                        type: AkitLearningEventType.HINT,
                                        marksTotal:
                                            obj.data.scoring?obj.data.score.marksTotal:-1,
                                        marksEarned:
                                            obj.data.scoring?obj.data.score.marksEarned:-1,
                                        finished: obj.data.scoring?obj.data.score.finished:false,
                                        tags: obj.data.tags,
                                    });
                                    break;
                                }
                                case "interaction-activated": {
                                    this.activated.emit();
                                    break;
                                }
                                case "interaction-submit-state-changed": {
                                    this.pristineChanged.emit({
                                        pristine: !obj.data,
                                    });
                                }
                            }
                        }
                    });
                });
                this.waitForRender().then(() => {
                    let elms =
                        this.container.nativeElement.getElementsByTagName(
                            "akit-interaction"
                        );
                    if (elms && elms.length == 1) {
                        AlgebraKIT.waitUntilReady(elms[0]).then(() => {
                            this._ngZone.run(() => {
                                this.initialized.emit();
                            });
                        });
                    }
                });
            });
    }

    ngOnInit(): void {}
 
    ngOnChanges() {
        this.rehydrate();
    }
    rehydrate(sessionId?:string) {
        if(!sessionId) sessionId = this._sessionId.value;
        if(!sessionId) return;

        this.session = null;
        this._sessionId.next(null);
        this.waitForRender().then(() => {
            this._sessionId.next(sessionId);
        });
    }

    ngAfterViewChecked(): void {
        if (this.renderResolve) {
            this.renderPromise = null;
            this.renderResolve();
        }
    }

    waitForRender(): Promise<void> {
        if (!this.renderPromise) {
            this.renderPromise = new Promise((resolve, reject) => {
                this.renderResolve = resolve;
            });
        }
        return this.renderPromise;
    }
}
